import React from 'react';
import { Box, CircularProgress, Stack, Typography, styled } from '@mui/material';
import { DateRangePickerProps } from 'components/DateRangePicker/DateRangePicker';
import { WidgetDetailLayout } from 'components/ProgressTab/WidgetDetail/WidgetDetailLayout';
import { ReadingRow } from 'components/ProgressTab/WidgetDetail/WidgetReadingList';
import { StepCountChart } from 'components/ProgressTab/Widgets/StepCountWidget/StepCountChart';
import {
  DAYS_IN_WEEK,
  MILES_PER_STEPS,
  StepCountWidgetProps,
  buildStepWeek,
  getChartMaximum,
} from 'components/ProgressTab/Widgets/StepCountWidget/StepCountWidget';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { longFormatDate, parseLocalDateString } from 'utils/dateUtils';

const Summary = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  gap: theme.spacing(5),
  marginBlock: theme.spacing(3),
  [theme.breakpoints.up('sm')]: { gap: theme.spacing(8), marginBlock: theme.spacing(4) },
}));

const Stat = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
});

const StatValue = styled(Typography)(({ theme }) => ({
  ...theme.typography.title3,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[900],
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.title1,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const StatUnit = styled('span')(({ theme }) => ({
  ...theme.typography.subheadline,
  fontWeight: theme.typography.fontWeightRegular,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[800],
  ...theme.typography.subheadline,
  [theme.breakpoints.up('sm')]: { ...theme.typography.callout },
}));

const formatRangeLabel = (start: string, end: string) => {
  const startLabel = parseLocalDateString(start).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const endLabel = parseLocalDateString(end).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${startLabel} - ${endLabel}`;
};

export interface StepCountDetailPageViewProps
  extends Pick<StepCountWidgetProps, 'physicalActivityStats'>,
    Pick<DateRangePickerProps, 'onPrevious' | 'onNext' | 'disablePrevious' | 'disableNext'> {
  /** Last day of the week shown (yyyy-MM-dd). */
  endDate: string;
  isLoading?: boolean;
  isError?: boolean;
  onBack: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
}

export const StepCountDetailPageView = ({
  physicalActivityStats,
  endDate,
  onPrevious,
  onNext,
  disablePrevious,
  disableNext,
  isLoading = false,
  isError = false,
  onBack,
  onDownload,
  isDownloading = false,
}: StepCountDetailPageViewProps) => {
  const { translation } = useTranslationContext();

  const week = buildStepWeek(physicalActivityStats, endDate);
  const startDate = week[0].date;
  const totalSteps = week.reduce((total, stat) => total + stat.passive_steps, 0);
  const average = Math.round(totalSteps / DAYS_IN_WEEK);

  const readings = React.useMemo<ReadingRow[]>(
    () =>
      physicalActivityStats
        .filter((stat) => stat.passive_steps > 0 && stat.date >= startDate && stat.date <= endDate)
        .sort((a, b) => b.date.localeCompare(a.date))
        .map((stat) => ({
          id: stat.date,
          value: stat.passive_steps,
          // TODO: no translation key for the "steps" unit yet
          unit: 'steps',
          dateLabel: longFormatDate(`${stat.date}T00:00:00`),
        })),
    [physicalActivityStats, startDate, endDate],
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <Stack alignItems="center" p={6}>
          <CircularProgress aria-label={translation('step_count')} />
        </Stack>
      );
    }

    if (isError) {
      return (
        <Stack alignItems="center" p={6}>
          <Typography variant="footnote">{translation('data_unable_to_load')}</Typography>
        </Stack>
      );
    }

    return (
      <Box role="img" aria-label={translation('step_count')} px={{ xs: 1, sm: 4 }}>
        <StepCountChart week={week} maximum={getChartMaximum(week)} />
      </Box>
    );
  };

  return (
    <WidgetDetailLayout
      title={translation('step_count')}
      onBack={onBack}
      dateRange={{
        label: formatRangeLabel(startDate, endDate),
        onPrevious,
        onNext,
        disablePrevious,
        disableNext,
        previousAriaLabel: translation('back_1_week_accessibility'),
        nextAriaLabel: translation('forward_1_week_accessibility'),
      }}
      summary={
        !isLoading &&
        !isError && (
          // TODO: translations for the "steps" / "miles" units and 'Edit daily step goal' - WFMOB-16750
          <Summary>
            <Stat>
              <StatValue>
                {average} <StatUnit>steps</StatUnit>
              </StatValue>
              <StatLabel>{translation('average_7_day')}</StatLabel>
            </Stat>
            <Stat>
              <StatValue>
                {(totalSteps / MILES_PER_STEPS).toFixed(1)} <StatUnit>miles</StatUnit>
              </StatValue>
              <StatLabel>{translation('total_7_day')}</StatLabel>
            </Stat>
          </Summary>
        )
      }
      readings={isLoading || isError ? [] : readings}
      onDownload={onDownload}
      isDownloading={isDownloading}
    >
      {renderBody()}
    </WidgetDetailLayout>
  );
};
